import _ from 'lodash';
import * as React from 'react';
import { HttpRequest } from '../../../util/http';
import { userData } from '../../../util/userData';
import { ControlledCheckbox } from "../ControlledCheckbox";
import { Collapse } from "reactstrap";
import { FontAwesomeIcon } from "../FontAwesomeIcon";
import { EmployeeInput } from 'containers/EmployeeInput/EmployeeInput';

interface IAvidsNestedRes {
    is_parent_of_lowest: number;
    is_lowest_level: number;
    value: string
    label: string
    nodes: IAvidsNestedRes[]
}
interface INestedAvidSelectProps {
    size?: 'sm' | 'lg';
    value: string;
    onChange: (value: string, label: string) => void;
    shouldFetch: boolean; // Add this prop
}

const getNestedAvids = _.throttle(
    () => {
        const method = 'POST'
        const url = new URL(window.location.href);
        const type = 'COACHINGFORM'
        const survey = url.searchParams.get('survey');
        const bypassUserFilter = true;
        const data = { type, survey, bypassUserFilter }

        return HttpRequest.fetch<any>('/web/v1/avids/get-nested-by-survey', { data, method })
    },
    500, { leading: true, trailing: false }
)

export const NestedAvidCheckbox: React.FC<INestedAvidSelectProps> = (props) => {


    const [nestedAvids, setNestedAvids] = React.useState<IAvidsNestedRes | undefined>(undefined);
    const [openedCollapseArray, setOpenedCollapseArray] = React.useState<string[]>([]);
    const [checkboxHasBeenClicked, setCheckboxHasBeenClicked] = React.useState<boolean>(false);
    const [inputVisibility, setInputVisibility] = React.useState<{ [key: string]: boolean }>({});
    const [newEmployee, setNewEmployee] = React.useState({ id: '', name: '' });



    React.useEffect(() => {
        if (props.shouldFetch) {
            getNestedAvids().then((res) => setNestedAvids(res.data[0]));
        }
    }, [props.shouldFetch]); // Only fetch when shouldFetch changes

    const isAnyChildChecked = (children: IAvidsNestedRes[]): boolean => {
        return children.some(child => {
            if (child.value === props.value) {
                return true;
            }
            if (child.nodes && child.nodes.length > 0) {
                return isAnyChildChecked(child.nodes);
            }
            return false;
        });
    };

    const addEmployeeToNode = (parentID: string, empID: string, empName: string) => {
        const data = { parentID, empName, empID };

        const method = 'POST';
        const url = '/web/v1/avids/add-dynamic-child';

        HttpRequest.fetch<any>(url, { data, method })
            .then((res) => {
                if (res.data.error > 0) {
                    alert("Failed to add record. Please try again.");
                    console.log("Error adding record:", res.data.error)
                } else {
                    getNestedAvids().then((res) => setNestedAvids(res.data[0]));
                    alert("Record added successfully.  You may now select this new record for survey re-assingment.");
                }
            })
            .catch((err) => {
                console.error("Error adding employee:", err);
                alert("An error occurred. Please try again later.");
            });
    };

    const getOptions = (children: IAvidsNestedRes[], depth = 0, elements: JSX.Element[] = []): JSX.Element[] => {
        children.forEach((child) => {

            const isChecked = child.value === props.value;
            const isOpen = openedCollapseArray.includes(child.value) || (isAnyChildChecked(child.nodes) && !checkboxHasBeenClicked);

            if (isOpen && !openedCollapseArray.includes(child.value)) {
                setOpenedCollapseArray([...openedCollapseArray, child.value]);
            }

            elements.push(
                <div style={{ paddingLeft: `${depth}em` }} key={`avid-checkbox-${child.value}`}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <ControlledCheckbox
                            isChecked={isChecked}
                            label={`${child.label}`}
                            key={child.value}
                            display={child.is_lowest_level === 1 ? 'inline' : 'none'}
                            title={`${child.label} (${child.value})`}
                            onChange={(isChecked) => {
                                if (isChecked) {
                                    props.onChange(child.value, child.label);
                                }
                            }}
                        />
                        &nbsp;&nbsp;
                        {child.nodes && child.nodes.length > 0 &&
                            <div style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={(isOpen ? 'minus' : 'plus')} size="sm" verticalAlign="middle"
                                    onClick={() => {
                                        setCheckboxHasBeenClicked(true);
                                        if (openedCollapseArray.includes(child.value)) {
                                            setOpenedCollapseArray(openedCollapseArray.filter((id) => id !== child.value));
                                        } else {
                                            setOpenedCollapseArray([...openedCollapseArray, child.value]);
                                        }
                                    }} />
                            </div>
                        }
                        &nbsp;&nbsp;
                        {child.is_parent_of_lowest === 1 &&
                            <div
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setInputVisibility((prev) => ({
                                        ...prev,
                                        [child.value]: !prev[child.value],
                                    }));
                                }}
                            >
                                <i className="fas fa-users"></i>
                            </div>
                        }
                    </div>

                    {inputVisibility[child.value] && (
                        <EmployeeInput
                            parentID={child.value}
                            onAddEmployee={(parentID, id, name) => addEmployeeToNode(parentID, id, name)}
                            onClose={() =>
                                setInputVisibility((prev) => ({
                                    ...prev,
                                    [child.value]: false,
                                }))
                            }
                        />
                    )}

                    <Collapse isOpen={isOpen}>
                        {getOptions(child.nodes, depth + 1)}
                    </Collapse>
                </div>
            );
        });

        return elements;
    };

    if (!nestedAvids) {
        return <h6 className="text-muted">Loading...</h6>;
    }

    const options = getOptions(nestedAvids.nodes);

    return (
        <div style={{ overflow: 'scroll', maxHeight: '30vh' }} className="blacklight">
            {options}
        </div>
    );
};
